import { CSSProperties, FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import UserPopup from "./UserPopup";

interface UserPopupWrapperProps {
    userId: number;
    children: ReactNode[] | ReactNode;
    style?: CSSProperties;
}

const UserPopupWrapper: FC<UserPopupWrapperProps> = ({ userId, children, style }) => {
    const RIGHT_BORDER_MARGIN = 20;
    const LEFT_BORDER_MARGIN = 20;

    const [showPopup, setShowPopup] = useState<boolean>(false);
    const [popupLoaded, setPopupLoaded] = useState<boolean>(false);
    const [popupPositioned, setPopupPositioned] = useState<boolean>(false);

    const elemRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);

    // Мемоизированный обработчик клика
    const handleClick = useCallback((e: React.MouseEvent) => {
        if (userId === undefined) return;
        setShowPopup(true);
        setPopupPositioned(false);
    }, [userId]);

    // Мемоизированный обработчик закрытия попапа
    const handleClosePopup = useCallback(() => {
        setShowPopup(false);
        setPopupLoaded(false);
    }, []);

    // Оптимизированная функция позиционирования попапа
    const positionPopup = useCallback(() => {
        const popup = popupRef.current;
        const elem = elemRef.current;
        if (!elem || !popup) return;

        const elemRect = elem.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        // Вертикальное позиционирование
        if (elemRect.top < viewportHeight / 2) {
            popup.style.top = `${elemRect.bottom + window.scrollY + 10}px`;
        } else {
            popup.style.top = `${elemRect.top - popup.offsetHeight + window.scrollY - 10}px`;
        }

        popup.style.position = "fixed";
        popup.style.zIndex = "100000";

        // Горизонтальное позиционирование с учетом границ экрана
        const baseLeft = elemRect.left - (elemRect.width * 1) / 2 + window.scrollX;
        const left = Math.min(
            Math.max(LEFT_BORDER_MARGIN, baseLeft),
            viewportWidth - popup.offsetWidth - RIGHT_BORDER_MARGIN
        );
        popup.style.left = `${left}px`;

        setPopupPositioned(true);
    }, []);

    useEffect(() => {
        if (!showPopup) return;
        positionPopup();
    }, [showPopup, popupLoaded, positionPopup]);

    // Обработчик ресайза для перепозиционирования попапа
    useEffect(() => {
        if (!showPopup) return;
        
        const handleResize = () => {
            if (showPopup && popupLoaded) {
                positionPopup();
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [showPopup, popupLoaded, positionPopup]);

    return (
        <div style={{ display: "flex", alignItems: "center", ...style }}>
            <div
                ref={elemRef}
                onClick={handleClick}
                style={{ 
                    width: "100%", 
                    height: "fit-content", 
                    display: "flex", 
                    alignItems: "center",
                    cursor: userId ? 'pointer' : 'default'
                }}
            >
                {children}
            </div>
            {userId && showPopup && (
                <div 
                    ref={popupRef}
                    style={{ 
                        visibility: popupPositioned ? 'visible' : 'hidden',
                        opacity: popupPositioned ? 1 : 0,
                        transition: 'opacity 0.1s ease-in-out'
                    }}
                >
                    <UserPopup
                        userId={userId}
                        onOverlayClick={handleClosePopup}
                        onLoaded={() => setPopupLoaded(true)}
                    />
                </div>
            )}
        </div>
    );
};

export default UserPopupWrapper;